import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ServicesGroupStore } from './services-group.store';
import {
    ProfessionalServiceGroupSelected,
    ServicesGroup,
} from './services-group.model';
import { Observable } from 'rxjs';
import { LanguageService } from 'src/app/core/services/language/language.service';
import { ApiService } from 'src/app/core/services/api/api.service';
import { ServiceWizardQuery } from '../service-wizard/service-wizard.query';
import { SettingsQuery } from '../settings/settings.query';
import { UserQuery } from '../user/user.query';
import { ClinicDetailQuery } from '../clinic-detail/clinic-detail.query';
import { ServicesGroupQuery } from './services-group.query';
import { ProfessionalWizard } from '../../models/professional-wizard.model';
import { AppointmentWizardSettingsQuery } from '../appointment-wizard-settings/appointment-wizard-settings.query';
import { ClinicWizardModel } from '../../models/clinic-wizard.model';
import { AppointmentWizardSettingsService } from '../appointment-wizard-settings/appointment-wizard-settings.service';

@Injectable({ providedIn: 'root' })
export class ServicesGroupService {
    constructor(
        private servicesGroupStore: ServicesGroupStore,
        private http: HttpClient,
        private languageService: LanguageService,
        private apiService: ApiService,
        private serviceWizardQuery: ServiceWizardQuery,
        private settingsQuery: SettingsQuery,
        private userQuery: UserQuery,
        private clinicDetailQuery: ClinicDetailQuery,
        private servicesGroupQuery: ServicesGroupQuery,
        private appointmentWizardSettingsQuery: AppointmentWizardSettingsQuery,
        private appointmentWizardSettingsService: AppointmentWizardSettingsService
    ) {}

    /*---------------------*/
    /** OPERACIONES ENTITY */
    /*---------------------*/

    add(servicesGroup: ServicesGroup) {
        this.servicesGroupStore.add(servicesGroup);
    }

    update(id, servicesGroup: Partial<ServicesGroup>) {
        this.servicesGroupStore.update(id, servicesGroup);
    }

    remove(id: ID) {
        this.servicesGroupStore.remove(id);
    }

    setServicesGroups(servicesGroups: ServicesGroup[]) {
        this.servicesGroupStore.set(servicesGroups);
    }

    /**
     * Factoría que crea un Servicio para el asistente de pedir cita con los datos recogidos por la API
     */
    servicesGroupFactory(
        params: any,
        order: number,
        professionalsWizard: ProfessionalWizard[]
    ) {
        let professionals: ProfessionalWizard[] = [];
        if (params.ServicesProfessionals) {
            params.ServicesProfessionals.forEach((currentProfessionalData) => {
                const newProfessional: ProfessionalWizard = {
                    id: currentProfessionalData.ProfessionalId,
                    description: currentProfessionalData.ProfessionalName,
                    clinic: [],
                    disabled: currentProfessionalData.UserDropdownsDisabled === '-1',
                };
                professionals.push(newProfessional);
            });
        }
        if (professionals[0].id === '') {
            /** Tengo que filtrar de entre todos los profesionales que me envía el back en la llamada c1041, solo aquellos
             * que estén en la misma clínica que los servicios o un 0 en su clinica */

            professionals = professionalsWizard?.filter((professional) => {
                return (
                    professional.clinic.includes(params.ServicesClinicID) ||
                    professional.clinic.includes('0')
                );
            });
        }

        return {
            id: params.ServicesID.join('-'),
            ids: params.ServicesID,
            clinicId: params.ServicesClinicID,
            clinicName: params.ServicesClinicName,
            descriptions: params.ServicesDescription,
            professionals,
            equipmentIds: params.ServicesEquipmentID,
            professionalSelected: null,
            order,
        } as ServicesGroup;
    }

    /*---------------*/
    /** LLAMADAS API */
    /*---------------*/

    /**
     * Llamada api para obtener los grupos de servicios
     */
    private getServicesGroupApi(
        systemKey: string,
        clientToken: string,
        clinicId: string,
        services: string
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append('SystemKey', systemKey);
        params = params.append('ClientToken', clientToken);
        params = params.append('ClinicID', clinicId);
        params = params.append('Services', services);
        params = params.append('ProfessionalID', '');
        params = params.append('ProfessionalName', '');
        params = params.append('DiaryDate', '');
        params = params.append('TimeEnd', '');
        params = params.append('CabineID', '');
        params = params.append('TimeStart', '');
        params = params.append('cmd', 'c1045');
        params = params.append(
            'CompositeApp',
            undefined
        ); /** TODO: QUE ES ESTO */
        params = params.append('Locale', this.languageService.getLocale());

        return this.apiService.callApi(params);
    }

    /*------------*/
    /** SERVICIOS */
    /*------------*/
    /**
     * Llama a la API para obtener los grupos de servicios
     */
    async getServicesGroup() {
        /** Los servicios seleccionados los cogemos de Akita */
        const servicesChekedArrayId = [];
        const servicesCheked = this.serviceWizardQuery
            .getAll({
                filterBy: ({ checked }) => checked === true,
            })
            .sort((a, b) => {
                if (a.order < b.order) {
                    return -1;
                }
                if (a.order > b.order) {
                    return 1;
                }
                return 0;
            })
            .forEach((service) => {
                servicesChekedArrayId.push(service.id);
            });

        const servicesGroupData = await this.getServicesGroupApi(
            this.settingsQuery.systemKeyURL,
            this.userQuery.tokenId,
            this.clinicDetailQuery.id,
            servicesChekedArrayId.join(',')
        ).toPromise();

        /**
         * Cargamos las clínicas a la Store
         */
        this.setServicesGroupsByApi(servicesGroupData.ServicesGroups);
    }

    /**
     * Crea los grupos de servicios a partir de los datos de la API
     */
    setServicesGroupsByApi(groups: any[]) {
        const servicesGroups = [];
        const clinicWizardList: ClinicWizardModel[] = [];
        const professionalsWizard: ProfessionalWizard[] = this
            .appointmentWizardSettingsQuery.professionals;
        groups.forEach((group, index) => {
            const currentGroup = this.servicesGroupFactory(
                group,
                index,
                professionalsWizard
            );
            servicesGroups.push(currentGroup);

            /** TODO: Mirar si es necesario hacer esto, porque creo no hace falta para nada
             * En principio se hizo para poder mostrar las nombres de las clínicas en el resumen de cita */
            if (
                !clinicWizardList.some((clinicWizard) => {
                    return clinicWizard.id === currentGroup.clinicId;
                })
            ) {
                clinicWizardList.push({
                    id: currentGroup.clinicId,
                    description: currentGroup.clinicName,
                });
            }
        });

        this.appointmentWizardSettingsService.updateClinics(clinicWizardList);
        this.setServicesGroups(servicesGroups);
    }

    setProfessionalWizardChecked(
        professionalServiceGroupSelected: ProfessionalServiceGroupSelected
    ) {
        this.servicesGroupStore.update(
            professionalServiceGroupSelected.servicesGroupId,
            {
                professionalSelected:
                    professionalServiceGroupSelected.professional,
            }
        );
    }

    toggleProfessionalWizardChecked(
        professionalServiceGroupSelected: ProfessionalServiceGroupSelected
    ) {
        const currentServiceGroupStorage = this.servicesGroupQuery.getEntity(
            professionalServiceGroupSelected.servicesGroupId
        );
        if (
            currentServiceGroupStorage?.professionalSelected?.id ==
            professionalServiceGroupSelected.professional.id
        ) {
            this.servicesGroupStore.update(
                professionalServiceGroupSelected.servicesGroupId,
                {
                    professionalSelected: null,
                }
            );
        } else {
            this.servicesGroupStore.update(
                professionalServiceGroupSelected.servicesGroupId,
                {
                    professionalSelected:
                        professionalServiceGroupSelected.professional,
                }
            );
        }
    }

    resetProfessionalWizardCheckedSingleGroup() {
        this.servicesGroupStore.update(this.servicesGroupQuery.getAll()[0].id, {
            professionalSelected: null,
        });
    }

    resetStore() {
        this.servicesGroupStore.reset();
    }
}
